import { Button, Group, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import styled from 'styled-components';
import { PALETTE } from '@dealstack/shared/lib/common/business/stampDesign';
import { signatureSlotKey } from '@dealstack/shared/lib/common/business/signatureSlotKey';

import { SIGNATURE_DRAG_MIME, serialiseSignaturePayload } from './extensions/signatureDrop';
import type { SignatureSlotPayload } from './extensions/signatureDrop';
import { PaletteList } from './flowDocumentEditor.styled';

export interface SignatureSlotPaletteItem extends SignatureSlotPayload {
	label: string;
	/** True when the document already holds an anchor for this slot. */
	isPlaced: boolean;
}

interface Props {
	slots: SignatureSlotPaletteItem[];
	/** Inserts at the caret. Returns false when a block cannot go where the caret is. */
	onInsert: (slot: SignatureSlotPayload) => boolean;
	/** Inserts an empty two-column block at the caret, for signatures that sign side by side. */
	onInsertBlock: () => boolean;
	/** Scrolls the document to the anchor already placed for this slot. */
	onLocate: (slot: SignatureSlotPayload) => void;
	onRemove: (slot: SignatureSlotPayload) => void;
}

/**
 * The signatures this document has to carry, and whether each one has been placed yet.
 *
 * The list is derived from the template's signing roles (`enumerateSlots`), never from the
 * document — so a role added after the document was written shows up here as unplaced rather
 * than being silently absent. All of them or none of them: a document that places SOME cannot be
 * saved, because the signer with no anchor has nowhere for their stamp to go, while one that
 * places none falls back to the appended signatures page.
 *
 * Dragging carries a private MIME payload for the same reason the field palette's does: a slot
 * dropped as `text/plain` would land in any input on the page as meaningless JSON.
 *
 * Two signatures sign SIDE BY SIDE by going in a two-column block, which is what a Word
 * execution block already is — an anchor is a block node, so two of them left to themselves
 * stack. The pair button builds that container; the slots then go into its cells like any other.
 */
export const SignatureSlotPalette = ({
	slots,
	onInsert,
	onInsertBlock,
	onLocate,
	onRemove,
}: Props) => {
	const placedCount = slots.filter((slot) => slot.isPlaced).length;
	// The slot whose last Place was declined, so the reason can be shown against that row.
	const [declinedKey, setDeclinedKey] = useState<string | null>(null);
	const [blockDeclined, setBlockDeclined] = useState(false);

	return (
		<>
			<Text size="xs" c="dark.0" mb="xs">
				{placedCount === slots.length
					? `All ${slots.length} signature${slots.length === 1 ? '' : 's'} placed.`
					: `${placedCount} of ${slots.length} placed. Click one to put it at the cursor, or drag it into the document — drop it on a signature already placed to sit beside it.`}
			</Text>
			{slots.length > 0 && (
				<>
					{/* A signature is a block, so two of them stack. Side by side needs a
					    container with two columns — which a Word execution block already is, but
					    a document that arrived without one had no way to get one. Dragging one
					    stamp onto another builds the same container; this is the route for an
					    author who wants the block before they have anything to put in it. */}
					<Button
						variant="default"
						size="xs"
						mb="xs"
						data-testid="signature-block-insert"
						onClick={() => setBlockDeclined(!onInsertBlock())}
					>
						Add a side-by-side pair
					</Button>
					{blockDeclined && (
						<Text size="xs" c="red" mb="xs" data-testid="signature-block-declined">
							Put the cursor where the pair should go.
						</Text>
					)}
				</>
			)}
			{slots.length === 0 ? (
				<Text size="sm" c="dark.0" data-testid="signature-slot-palette-empty">
					This document has no signature roles configured, so there is nothing to place.
				</Text>
			) : (
				<PaletteList data-testid="signature-slot-palette">
					{slots.map((slot) => {
						const key = signatureSlotKey(slot);
						const payload: SignatureSlotPayload = {
							signingRoleId: slot.signingRoleId,
							isWitness: slot.isWitness,
							signatureIndex: slot.signatureIndex,
						};
						return (
							<SlotRow
								key={key}
								$placed={slot.isPlaced}
								data-testid={`signature-slot-${key}`}
								// Only an unplaced slot is draggable: a second anchor for the
								// same slot resolves to two boxes and only one can be stored,
								// so the other stamp would silently not happen.
								draggable={!slot.isPlaced}
								onDragStart={(event) => {
									event.dataTransfer.setData(
										SIGNATURE_DRAG_MIME,
										serialiseSignaturePayload(payload)
									);
									event.dataTransfer.effectAllowed = 'copy';
								}}
							>
								<Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
									<Dot $filled={slot.isPlaced} />
									<Stack gap={0} style={{ minWidth: 0 }}>
										<Text size="sm" truncate>
											{slot.label}
										</Text>
										{declinedKey === key && (
											<Text
												size="xs"
												c="red"
												data-testid={`signature-slot-declined-${key}`}
											>
												Put the cursor where the signature should go, or
												drag it into the document.
											</Text>
										)}
									</Stack>
								</Group>
								{slot.isPlaced ? (
									<Group gap={4} wrap="nowrap">
										<Button
											variant="subtle"
											size="xs"
											onClick={() => onLocate(payload)}
										>
											Locate
										</Button>
										<Button
											variant="subtle"
											color="red"
											size="xs"
											onClick={() => onRemove(payload)}
										>
											Remove
										</Button>
									</Group>
								) : (
									<Button
										variant="filled"
										size="xs"
										// The insert can decline — a caret in a structural gap has
										// nowhere a block can go. Reported here, because otherwise
										// the button is exactly the "appears to do nothing" its
										// return value exists to prevent.
										onClick={() => {
											setDeclinedKey(onInsert(payload) ? null : key);
										}}
									>
										Place
									</Button>
								)}
							</SlotRow>
						);
					})}
				</PaletteList>
			)}
		</>
	);
};

const SlotRow = styled.div<{ $placed: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
	width: 100%;
	padding: 0.5rem 0.75rem;
	border-radius: 4px;
	cursor: ${({ $placed }) => ($placed ? 'default' : 'grab')};

	&:hover {
		background-color: ${({ theme }) => theme.v3.colors.gray[1]};
	}
`;

const Dot = styled.span<{ $filled: boolean }>`
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background-color: ${({ $filled }) => ($filled ? PALETTE.brand : 'transparent')};
	border: 1.5px solid
		${({ $filled, theme }) => ($filled ? PALETTE.brand : theme.v3.colors.gray[5])};
	flex-shrink: 0;
`;
